/**
 * Shop Page - Product Browse & Search with Sidebar Filters
 */
import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Slider } from '../components/ui/slider';
import { toast } from 'sonner';
import api from '../lib/api';
import ProductCard3D from '../components/ProductCard3D';
import { 
  Link2, 
  Search,
  ShoppingCart,
  Star,
  ArrowLeft,
  Filter,
  Package,
  Grid3X3,
  LayoutList,
  Store,
  TrendingUp,
  X,
  SlidersHorizontal,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

const Shop = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [featuredStores, setFeaturedStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || '');
  const [cartCount, setCartCount] = useState(0);
  
  // Filter states
  const [showFilters, setShowFilters] = useState(true);
  const [priceRange, setPriceRange] = useState([0, 50000]);
  const [maxPrice, setMaxPrice] = useState(50000);
  const [sortBy, setSortBy] = useState('newest');
  const [inStockOnly, setInStockOnly] = useState(false);
  const [rating, setRating] = useState(0);
  const [viewMode, setViewMode] = useState('grid');

  useEffect(() => {
    fetchProducts();
    fetchCategories();
    fetchCartCount();
    fetchFeaturedStores();
  }, [searchParams, priceRange, sortBy, inStockOnly, rating]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchParams.get('q')) params.append('search', searchParams.get('q'));
      if (searchParams.get('category')) params.append('category', searchParams.get('category'));
      if (priceRange[0] > 0) params.append('min_price', priceRange[0]);
      if (priceRange[1] < maxPrice) params.append('max_price', priceRange[1]);
      if (sortBy) params.append('sort', sortBy);
      if (inStockOnly) params.append('in_stock', 'true');
      if (rating > 0) params.append('min_rating', rating);
      
      const response = await api.get(`/products/public?${params.toString()}`);
      setProducts(response.data.products || response.data || []);
      
      // Set max price from products
      if (response.data.products?.length > 0) {
        const prices = response.data.products.map(p => p.price || 0);
        const max = Math.max(...prices, 50000);
        setMaxPrice(Math.ceil(max / 1000) * 1000);
      }
    } catch (error) {
      console.error('Failed to load products:', error);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get('/products/categories');
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  };

  const fetchCartCount = async () => {
    try {
      const response = await api.get('/cart/');
      setCartCount(response.data.items?.length || 0);
    } catch (error) {
      // Not logged in, ignore
    }
  };

  const fetchFeaturedStores = async () => {
    try {
      const response = await api.get('/admin/featured-stores/public?limit=8');
      setFeaturedStores(response.data || []);
    } catch (error) {
      console.log('Using placeholder stores');
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (searchQuery) params.set('q', searchQuery);
    if (selectedCategory) params.set('category', selectedCategory);
    setSearchParams(params);
  };

  const handleCategoryClick = (category) => {
    const params = new URLSearchParams();
    if (searchQuery) params.set('q', searchQuery);
    if (category !== selectedCategory) {
      params.set('category', category);
      setSelectedCategory(category);
    } else {
      setSelectedCategory('');
    }
    setSearchParams(params);
  };

  const addToCart = async (productId) => {
    try {
      await api.post('/cart/add', { product_id: productId, quantity: 1 });
      toast.success('Added to cart!');
      fetchCartCount();
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Please login to add items to cart');
        navigate('/login');
      } else {
        toast.error('Failed to add to cart');
      }
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-emerald-600 text-white px-6 py-4 sticky top-0 z-50" data-testid="shop-header">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate('/dashboard')} className="text-white hover:bg-emerald-700 p-2">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <Link2 className="h-6 w-6" />
            <span className="font-bold text-lg"> Shop</span>
          </div>
          <Button
            data-testid="cart-btn"
            onClick={() => navigate('/cart')}
            variant="ghost"
            className="text-white hover:bg-emerald-700 relative"
          >
            <ShoppingCart className="h-5 w-5" />
            {cartCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-white text-emerald-600 text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold">
                {cartCount}
              </span>
            )}
          </Button>
        </div>
      </header>

      {/* Search Bar */}
      <div className="bg-emerald-50 border-b border-emerald-100 px-6 py-4">
        <div className="max-w-6xl mx-auto">
          <form onSubmit={handleSearch} className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                data-testid="search-input"
                type="text"
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-white border-emerald-200 h-11 rounded-xl"
              />
            </div>
            <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl px-6">
              Search
            </Button>
          </form>
        </div>
      </div>

      {/* Categories */}
      <div className="px-6 py-3 border-b border-gray-100 overflow-x-auto">
        <div className="max-w-6xl mx-auto flex gap-2">
          <button
            onClick={() => handleCategoryClick('')}
            className={`px-4 py-2 rounded-full text-sm whitespace-nowrap transition-colors ${
              !selectedCategory ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-emerald-100'
            }`}
          >
            All
          </button>
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => handleCategoryClick(cat)}
              className={`px-4 py-2 rounded-full text-sm whitespace-nowrap transition-colors ${
                selectedCategory === cat ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-emerald-100'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* Featured Stores - Dynamic from Admin */}
      <section className="max-w-6xl mx-auto px-6 py-6 border-b border-gray-100">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-bold text-gray-800 flex items-center gap-2">
            <Store className="h-5 w-5 text-emerald-600" />
            Featured Stores
          </h2>
          <Button variant="link" className="text-emerald-600" onClick={() => navigate('/stores')}>
            View All
          </Button>
        </div>
        <div className="flex gap-4 overflow-x-auto pb-2">
          {featuredStores.length > 0 ? (
            featuredStores.map((store) => (
              <div 
                key={store.id}
                onClick={() => navigate(`/store/${store.store_id}`)}
                className="flex-shrink-0 w-40 p-4 bg-gradient-to-br from-emerald-50 to-white rounded-xl border border-emerald-100 cursor-pointer hover:shadow-md transition-all text-center"
                data-testid={`shop-featured-store-${store.store_id}`}
              >
                <div className="w-14 h-14 rounded-full bg-emerald-100 mx-auto mb-2 flex items-center justify-center overflow-hidden">
                  {store.store_logo ? (
                    <img 
                      src={store.store_logo.startsWith('http') ? store.store_logo : `${process.env.REACT_APP_BACKEND_URL}${store.store_logo}`}
                      alt={store.store_name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <Store className="w-6 h-6 text-emerald-600" />
                  )}
                </div>
                <p className="font-medium text-gray-800 text-sm truncate">{store.store_name}</p>
                <div className="flex items-center justify-center gap-1 text-xs text-gray-500 mt-1">
                  <Star className="w-3 h-3 fill-emerald-500 text-emerald-500" />
                  {store.rating?.toFixed(1) || '4.5'}
                </div>
                {store.location && (
                  <p className="text-xs text-gray-400 truncate mt-0.5">{store.location}</p>
                )}
              </div>
            ))
          ) : (
            // Fallback placeholder stores
            [1, 2, 3, 4].map(i => (
              <div 
                key={i}
                onClick={() => navigate(`/store/store-${i}`)}
                className="flex-shrink-0 w-40 p-4 bg-gradient-to-br from-emerald-50 to-white rounded-xl border border-emerald-100 cursor-pointer hover:shadow-md transition-all text-center"
              >
                <div className="w-14 h-14 rounded-full bg-emerald-100 mx-auto mb-2 flex items-center justify-center">
                  <Store className="w-6 h-6 text-emerald-600" />
                </div>
                <p className="font-medium text-gray-800 text-sm truncate">Store {i}</p>
                <div className="flex items-center justify-center gap-1 text-xs text-gray-500 mt-1">
                  <Star className="w-3 h-3 fill-emerald-500 text-emerald-500" />
                  4.{5 + i}
                </div>
              </div>
            ))
          )}
        </div>
      </section>

      {/* Main Content with Sidebar */}
      <div className="max-w-7xl mx-auto px-4 py-6 flex gap-6">
        {/* Filter Sidebar */}
        <aside className={`${showFilters ? 'w-64' : 'w-0'} flex-shrink-0 transition-all overflow-hidden`}>
          <div className="bg-white rounded-xl border border-gray-200 p-4 sticky top-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-gray-800 flex items-center gap-2">
                <SlidersHorizontal className="h-4 w-4" />
                Filters
              </h3>
              <button onClick={() => {
                setPriceRange([0, maxPrice]);
                setRating(0);
                setInStockOnly(false);
                setSortBy('newest');
              }} className="text-xs text-emerald-600 hover:underline">
                Clear All
              </button>
            </div>

            {/* Price Range */}
            <div className="mb-6">
              <h4 className="text-sm font-medium text-gray-700 mb-3">Price Range</h4>
              <div className="px-2">
                <Slider
                  value={priceRange}
                  onValueChange={setPriceRange}
                  max={maxPrice}
                  step={100}
                  className="mb-2"
                />
                <div className="flex items-center justify-between text-sm text-gray-600">
                  <span>J${priceRange[0].toLocaleString()}</span>
                  <span>J${priceRange[1].toLocaleString()}</span>
                </div>
              </div>
            </div>

            {/* Sort By */}
            <div className="mb-6">
              <h4 className="text-sm font-medium text-gray-700 mb-3">Sort By</h4>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full p-2 border border-gray-200 rounded-lg text-sm"
              >
                <option value="newest">Newest First</option>
                <option value="price_low">Price: Low to High</option>
                <option value="price_high">Price: High to Low</option>
                <option value="rating">Highest Rated</option>
                <option value="popular">Most Popular</option>
              </select>
            </div>

            {/* Rating Filter */}
            <div className="mb-6">
              <h4 className="text-sm font-medium text-gray-700 mb-3">Minimum Rating</h4>
              <div className="space-y-2">
                {[4, 3, 2, 1, 0].map((r) => (
                  <label key={r} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="rating"
                      checked={rating === r}
                      onChange={() => setRating(r)}
                      className="text-emerald-600"
                    />
                    <div className="flex items-center gap-1">
                      {r > 0 ? (
                        <>
                          {[...Array(r)].map((_, i) => (
                            <Star key={i} className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                          ))}
                          <span className="text-xs text-gray-500">& up</span>
                        </>
                      ) : (
                        <span className="text-sm text-gray-600">All Ratings</span>
                      )}
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* In Stock Only */}
            <div className="mb-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={inStockOnly}
                  onChange={(e) => setInStockOnly(e.target.checked)}
                  className="rounded text-emerald-600"
                />
                <span className="text-sm text-gray-700">In Stock Only</span>
              </label>
            </div>

            {/* Categories in sidebar */}
            <div className="border-t pt-4 mt-4">
              <h4 className="text-sm font-medium text-gray-700 mb-3">Categories</h4>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="category"
                    checked={!selectedCategory}
                    onChange={() => handleCategoryClick('')}
                    className="text-emerald-600"
                  />
                  <span className="text-sm text-gray-600">All Categories</span>
                </label>
                {categories.map((cat) => (
                  <label key={cat} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="category"
                      checked={selectedCategory === cat}
                      onChange={() => handleCategoryClick(cat)}
                      className="text-emerald-600"
                    />
                    <span className="text-sm text-gray-600">{cat}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </aside>

        {/* Products Grid */}
        <main className="flex-1" data-testid="products-grid">
          {/* Toolbar */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-lg text-sm hover:bg-gray-200"
              >
                <Filter className="w-4 h-4" />
                {showFilters ? 'Hide' : 'Show'} Filters
              </button>
              <span className="text-gray-600 text-sm">
                <TrendingUp className="w-4 h-4 inline mr-1" />
                {products.length} products
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded ${viewMode === 'grid' ? 'bg-emerald-100 text-emerald-600' : 'text-gray-400'}`}
              >
                <Grid3X3 className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded ${viewMode === 'list' ? 'bg-emerald-100 text-emerald-600' : 'text-gray-400'}`}
              >
                <LayoutList className="w-4 h-4" />
              </button>
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin h-8 w-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-12">
              <Package className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-gray-800 mb-2">No Products Found</h2>
              <p className="text-gray-600">Try adjusting your search or filters</p>
              <Button onClick={() => {
                setPriceRange([0, maxPrice]);
                setRating(0);
                setInStockOnly(false);
                setSelectedCategory('');
                setSearchQuery('');
                setSearchParams({});
              }} variant="outline" className="mt-4">
                Clear All Filters
              </Button>
            </div>
          ) : (
            <div className={`grid gap-4 ${viewMode === 'grid' ? 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4' : 'grid-cols-1'}`}>
              {products.map((product) => (
                <div
                  key={product.id}
                  data-testid={`product-card-${product.id}`}
                  className={`bg-white rounded-xl border-2 border-emerald-100 overflow-hidden hover:border-emerald-300 hover:shadow-lg transition-all cursor-pointer group ${viewMode === 'list' ? 'flex' : ''}`}
                >
                  <div 
                    onClick={() => navigate(`/product/${product.id}`)}
                    className={`bg-gray-100 overflow-hidden relative ${viewMode === 'list' ? 'w-32 h-32' : 'aspect-square'}`}
                  >
                    {product.images?.[0] ? (
                      <img 
                        src={product.images[0]} 
                        alt={product.title || product.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Package className="h-12 w-12 text-gray-300" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-emerald-500/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                    {product.free_shipping && (
                      <div className="absolute top-2 right-2 bg-green-600 text-white text-xs px-2 py-0.5 rounded-full font-medium">
                        Free Ship
                      </div>
                    )}
                  </div>
                  <div className={`p-3 ${viewMode === 'list' ? 'flex-1 flex flex-col justify-between' : ''}`}>
                    {product.business_name && (
                      <p className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                        <Store className="w-3 h-3" />
                        {product.business_name}
                      </p>
                    )}
                    <h3 
                      onClick={() => navigate(`/product/${product.id}`)}
                      className="font-medium text-gray-800 text-sm line-clamp-2 hover:text-emerald-600"
                    >
                      {product.title || product.name}
                  </h3>
                  <div className="flex items-center gap-1 mt-1">
                    <Star className="h-3 w-3 fill-emerald-500 text-emerald-500" />
                    <span className="text-xs text-gray-500">
                      {product.rating_average?.toFixed(1) || '5.0'} ({product.rating_count || 0})
                    </span>
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <p className="font-bold text-emerald-600">
                      J${(product.price || 0).toLocaleString()}
                    </p>
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        addToCart(product.id);
                      }}
                      size="sm"
                      className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg h-8 px-3"
                    >
                      <ShoppingCart className="h-3 w-3" />
                    </Button>
                  </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default Shop;
